import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Upload, CheckCircle, XCircle, Eye, EyeOff } from "lucide-react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";

const BACKEND_URL = "http://127.0.0.1:8000";
const API = `${BACKEND_URL}/api/v1`;

const PasswordRule = ({ ok, text }) => (
  <div className="flex items-center gap-2 text-xs">
    {ok ? <CheckCircle className="w-3 h-3 text-green-600" /> : <XCircle className="w-3 h-3 text-red-400" />}
    <span className={ok ? "text-green-700" : "text-muted-foreground"}>{text}</span>
  </div>
);

const FileInput = ({ label, required, onChange, value }) => (
  <div>
    <Label>{label}{required && " *"}</Label>
    <label className="mt-2 flex items-center gap-3 w-full border border-dashed border-input rounded-sm px-4 py-3 cursor-pointer hover:border-accent transition-colors">
      <Upload className="w-4 h-4 text-muted-foreground shrink-0" />
      <span className="text-sm text-muted-foreground truncate">
        {value ? value.name : "Click to upload…"}
      </span>
      <input type="file" accept="image/*,.pdf" className="hidden" onChange={onChange} />
    </label>
  </div>
);

export const RegisterPage = () => {
  const navigate = useNavigate();
  useAuth();
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [form, setForm] = useState({
    first_name: "", last_name: "",
    email: "", company_name: "", company_email: "",
    phone: "", business_address: "", ein_number: "",
    password: "", confirm_password: "",
  });
  const [idDocument, setIdDocument] = useState(null);
  const [einCertificate, setEinCertificate] = useState(null);

  const pw = form.password;
  const pwRules = {
    length: pw.length >= 8,
    upper: /[A-Z]/.test(pw),
    lower: /[a-z]/.test(pw),
    number: /\d/.test(pw),
  };
  const pwValid = Object.values(pwRules).every(Boolean);

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!pwValid) { toast.error("Password does not meet requirements"); return; }
    if (form.password !== form.confirm_password) { toast.error("Passwords do not match"); return; }
    if (!idDocument) { toast.error("Please upload your government ID"); return; }
    if (!einCertificate) { toast.error("Please upload your EIN certificate"); return; }

    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (k !== "confirm_password") fd.append(k, v); });
      fd.append("id_document", idDocument);
      fd.append("ein_certificate", einCertificate);

      const res = await axios.post(`${API}/auth/register`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // Store tokens manually then let AuthContext pick up user
      localStorage.setItem("access_token", res.data.access_token);
      localStorage.setItem("refresh_token", res.data.refresh_token);

      toast.success("Account created! Please check your email to verify your address.");
      navigate("/verify-pending");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail.map(e => `${e.loc?.at(-1)}: ${e.msg}`).join(", ") : detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const Field = ({ label, name, type = "text", required = true, placeholder = "" }) => (
    <div>
      <Label htmlFor={name}>{label}{required && " *"}</Label>
      <input
        id={name} name={name} type={type}
        value={form[name]} onChange={handleChange}
        placeholder={placeholder} required={required}
        className="w-full mt-2 border border-input rounded-sm px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-accent"
      />
    </div>
  );

  return (
    <div className="min-h-screen bg-secondary py-16 px-6">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-10">
          <h1 className="font-heading text-4xl font-bold mb-2">Apply for a Business Account</h1>
          <p className="text-muted-foreground text-sm">
            Fill in your business details. Our team will review and approve your account within 1–2 business days.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white border border-border rounded-md p-8 space-y-6">

          {/* Personal info */}
          <div>
            <h2 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-4">Personal Information</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="First Name" name="first_name" />
              <Field label="Last Name" name="last_name" />
              <div className="md:col-span-2">
                <Field label="Personal Email" name="email" type="email" />
              </div>
              <div className="md:col-span-2">
                <Field label="Phone Number" name="phone" type="tel" placeholder="+1 (555) 000-0000" />
              </div>
            </div>
          </div>

          {/* Business info */}
          <div className="border-t border-border pt-6">
            <h2 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-4">Business Information</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Field label="Company Name" name="company_name" />
              </div>
              <div className="md:col-span-2">
                <Field label="Company Email" name="company_email" type="email" />
              </div>
              <div className="md:col-span-2">
                <Field label="Business Address" name="business_address" placeholder="123 Main St, Los Angeles, CA 90015" />
              </div>
              <div className="md:col-span-2">
                <Field label="EIN Number" name="ein_number" placeholder="XX-XXXXXXX" />
              </div>
            </div>
          </div>

          {/* Documents */}
          <div className="border-t border-border pt-6">
            <h2 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-4">Verification Documents</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <FileInput
                label="Government-issued ID"
                name="id_document"
                required
                value={idDocument}
                onChange={e => setIdDocument(e.target.files[0] || null)}
              />
              <FileInput
                label="EIN Certificate"
                name="ein_certificate"
                required
                value={einCertificate}
                onChange={e => setEinCertificate(e.target.files[0] || null)}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-2">Accepted formats: JPG, PNG, PDF</p>
          </div>

          {/* Password */}
          <div className="border-t border-border pt-6">
            <h2 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-4">Set Password</h2>
            <div className="space-y-4">
              <div>
                <Label htmlFor="password">Password *</Label>
                <div className="relative mt-2">
                  <input
                    id="password" name="password" type={showPass ? "text" : "password"}
                    value={form.password} onChange={handleChange} required
                    className="w-full border border-input rounded-sm px-4 py-3 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-accent"
                  />
                  <button type="button" onClick={() => setShowPass(p => !p)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <div className="mt-2 space-y-1">
                  <PasswordRule ok={pwRules.length} text="At least 8 characters" />
                  <PasswordRule ok={pwRules.upper} text="One uppercase letter" />
                  <PasswordRule ok={pwRules.lower} text="One lowercase letter" />
                  <PasswordRule ok={pwRules.number} text="One number" />
                </div>
              </div>
              <div>
                <Label htmlFor="confirm_password">Confirm Password *</Label>
                <div className="relative mt-2">
                  <input
                    id="confirm_password" name="confirm_password" type={showConfirm ? "text" : "password"}
                    value={form.confirm_password} onChange={handleChange} required
                    className="w-full border border-input rounded-sm px-4 py-3 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-accent"
                  />
                  <button type="button" onClick={() => setShowConfirm(p => !p)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {form.confirm_password && form.password !== form.confirm_password && (
                  <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
                )}
              </div>
            </div>
          </div>

          <Button type="submit" disabled={loading}
            className="w-full bg-accent hover:bg-accent-hover text-white py-6 text-sm tracking-wider">
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</> : "Submit Application"}
          </Button>

          <p className="text-center text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" className="text-accent font-medium hover:underline">Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default RegisterPage;
